import { useEffect } from 'react';
import { persistedStore } from 'lib/util';
import { CreateAddressInput, MealPreferencesModel } from 'lib/home/generated/types/api';

export enum RegistrationStep {
  unsupportedPostalCode = 'unsupportedPostalCode',
  index = 'index',
  preferences = 'preferences',
  createAccount = 'createAccount',
  checkEmail = 'checkEmail',
  selectPlan = 'selectPlan',
  customizeBox = 'customizeBox',
  swapMeal = 'swapMeal',
  orderSummary = 'orderSummary',
  success = 'success'
}

export const registrationStepsOrder = [
  RegistrationStep.index,
  RegistrationStep.selectPlan,
  RegistrationStep.preferences,
  RegistrationStep.createAccount,
  RegistrationStep.checkEmail,
  RegistrationStep.customizeBox,
  RegistrationStep.orderSummary,
  RegistrationStep.success
];

export const RegistrationRouteMap = {
  [RegistrationStep.index]: '/getting-started',
  [RegistrationStep.unsupportedPostalCode]: '/getting-started/unsupported-postal-code',
  [RegistrationStep.preferences]: '/getting-started/preferences',
  [RegistrationStep.createAccount]: '/getting-started/create-account',
  [RegistrationStep.checkEmail]: '/getting-started/check-email',
  [RegistrationStep.selectPlan]: '/getting-started/select-plan',
  [RegistrationStep.customizeBox]: '/getting-started/customize-box',
  [RegistrationStep.swapMeal]: '/getting-started/swap-meal',
  [RegistrationStep.orderSummary]: '/getting-started/order-summary',
  [RegistrationStep.success]: '/getting-started/success'
};

const initialState = {
  activeStep: RegistrationStep.index,
  checkoutInfo: {
    postal_code: ''
  },
  preferences: {},
  selectedPlan: '',
  promoCode: null,
  selectedPlanName: '',
  showCheckoutItems: false
};

export interface CheckoutAddress extends Omit<CreateAddressInput, 'first_name' | 'last_name'> {}

export interface RegistrationCheckoutInfo {
  first_name?: string;
  last_name?: string;
  postal_code: string;
  email?: string;
  shipping_address?: CheckoutAddress;
  billing_address?: CheckoutAddress;
  same_as_shipping?: boolean;
  hashed_password?: string;
}

export interface RegistrationStore {
  activeStep: RegistrationStep;
  checkoutInfo: RegistrationCheckoutInfo;
  preferences: MealPreferencesModel;
  selectedPlan: string;
  selectedPlanName: string;
  showCheckoutItems?: boolean;
  promoCode?: string;
  referralCode?: string;
  setActiveStep: (activeStep: RegistrationStep) => void;
  setCheckoutInfo: (checkoutInfo: RegistrationCheckoutInfo) => void;
  setPromoCode: (promoCode: string) => void;
  setReferralCode: (referralCode: string) => void;
  setPreferences: (preferences: MealPreferencesModel) => void;
  setSelectedPlan: (selectedPlan: string) => void;
  setSelectedPlanName: (selectedPlanName: string) => void;
  setShowCheckoutItems: (showCheckoutItems: boolean) => void;
  reset: () => void;
}

export const useRegistrationStore = persistedStore<RegistrationStore>(
  'REGISTRATION',
  set => {
    const methods = {
      setActiveStep: activeStep => set({ activeStep }),
      setCheckoutInfo: checkoutInfo =>
        set(state => {
          state.checkoutInfo = { ...state.checkoutInfo, ...checkoutInfo };
          return state;
        }),
      setSelectedPlan: selectedPlan =>
        set(state => {
          state.selectedPlan = selectedPlan;
          return state;
        }),
      setSelectedPlanName: selectedPlanName => set(state => ({ ...state, selectedPlanName })),
      setPreferences: preferences =>
        set(state => {
          state.preferences = { ...state.preferences, ...preferences };
          return state;
        }),

      setPromoCode: promoCode => set(state => ({ ...state, promoCode })),
      setReferralCode: referralCode => set(state => ({ ...state, referralCode })),
      setShowCheckoutItems: showCheckoutItems =>
        set(state => {
          state.showCheckoutItems = showCheckoutItems;
          return state;
        }),
      reset: () => set({ ...initialState, ...methods }, true)
    };

    return {
      ...initialState,
      ...methods
    };
  },
  {
    persist: {
      // IMPORTANT: DO NOT persist the password info
      omit: ['data.checkoutInfo.new_password', 'data.checkoutInfo.confirm_password']
    }
  }
);

export const useActiveStep = (step: RegistrationStep) => {
  const { setActiveStep } = useRegistrationStore();
  useEffect(() => {
    if (step && setActiveStep) setActiveStep(step);
  }, []);
};
